import React, { useEffect, useState } from 'react'
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ShoppingBag } from "@mui/icons-material";
import { useCart } from "../../context/CartContext";
import { getCartItems } from '../../actions/cartActions'
import { createNewOrder } from '../../actions/orderActions.js';
import { fetchMeta } from "../../actions/metaActions";
import { Helmet } from "react-helmet-async";
import "./Checkout.css";

export default function Checkout() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const { cartItems: items, clearAll } = useCart();
    const { cartItems, subtotal, total, error, loading } = useSelector(state => state.cartState);
    const { loading: orderLoading, order, error: OrderError } = useSelector((state) => state.orderState);
    const { meta, loading: metaLoading } = useSelector((state) => state.metaState);

    useEffect(() => {
        dispatch(fetchMeta('checkout'));
    }, [dispatch]);

    useEffect(() => {
        if (items)
            dispatch(getCartItems(items));
    }, [dispatch, items]);

    const [formData, setFormData] = useState({
        fullName: "",
        country: "UAE",
        phone: "",
        email: "",
        city: "",
        fullAddress: "",
        landmark: false,
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = "Full name is required";
        }

        if (!formData.phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (!/^\+?\d{7,15}$/.test(formData.phone)) {
            newErrors.phone = "Enter a valid phone number";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Enter a valid email address";
        }

        if (!formData.fullAddress.trim()) {
            newErrors.fullAddress = "Address is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const orderPayload = {
            customer: {
                fullName: formData?.fullName,
                email: formData?.email,
                phone: formData?.phone,
                fullAddress: formData?.fullAddress,
            },
            products: cartItems.map((item) => ({
                name: item?.productName,
                image: item?.image,
                brand: item?.brand,
                color: item?.color,
                size: item?.size,
                price: item?.price,
                quantity: item?.qty,
            })),
        };
        dispatch(createNewOrder(orderPayload));
    };

    useEffect(() => {
        if (order) {
            clearAll();
            navigate("/order-success");
        }

        if (error) {
            navigate("/order-error");
        }
    }, [order, error, navigate, clearAll]);

    return (
        <>
            <Helmet>
                <title>{meta?.title}</title>
                <meta name="description" content={meta?.metaDescription} />
                <meta name="keywords" content={meta?.keywords} />
                <link rel="canonical" href={meta?.canonicalUrl} />
                <meta name="robots" content={meta?.robots} />
            </Helmet>
            <div className="custom-container">
                {cartItems && cartItems?.length > 0 ?
                    <div className="checkout-page py-5">
                        {/* LEFT SIDE FORM */}
                        <div className="checkout-left">
                            <form onSubmit={handleSubmit} className="shipping-form">
                                <h3>Shipping Address</h3>
                                {/* FULL NAME */}
                                <div className="form-group full-width">
                                    <label>Full Name *</label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        required
                                    />
                                    {errors.fullName && <small className="error">{errors.fullName}</small>}
                                </div>


                                {/* EMAIL + PHONE */}
                                <div className="form-row">
                                    <div className="form-group half">
                                        <label>Telephone / Mobile *</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+971"
                                            required
                                        />
                                        {errors.phone && <small className="error">{errors.phone}</small>}
                                    </div>
                                    <div className="form-group half">
                                        <label>Email Address *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="example@email.com"
                                            required
                                        />
                                        {errors.email && <small className="error">{errors.email}</small>}
                                    </div>
                                </div>

                                {/* FULL ADDRESS */}
                                <div className="form-group full-width">
                                    <label>Full Address *</label>
                                    <textarea
                                        name="fullAddress"
                                        value={formData.fullAddress}
                                        onChange={handleChange}
                                        placeholder="House / Building / Street / Landmark"
                                        rows="3"
                                        required
                                    ></textarea>
                                    {errors.fullAddress && (
                                        <small className="error">{errors.fullAddress}</small>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="checkout-btn"
                                    disabled={orderLoading}
                                >
                                    {orderLoading ? (
                                        "Placing your order..."
                                    ) : (
                                        <>
                                            <ShoppingBag /> Place Order
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* RIGHT SIDE SUMMARY */}
                        {/* <div className="checkout-right">
                            <OrderSummary cartItems={cartItems} triggerSubmit={handleSubmit} loading={orderLoading} />
                        </div> */}
                    </div> :
                    <div className="d-flex flex-column justify-content-center align-items-center">
                        <div className="empty-cart text-center py-5">
                            <ShoppingBag style={{ fontSize: 80, color: "#ccc" }} />
                            <h2>Your cart is empty</h2>
                            <p>Looks like you haven’t added anything to your cart yet.</p>
                            <button
                                className="btn success-btn mt-3"
                                onClick={() => navigate("/")}
                            >
                                Continue Shopping
                            </button>
                        </div>
                    </div>
                }
            </div>
        </>
    );
}
